// src/components/Users/UserList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import UserModal from './UserModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import {
  Users,
  UserPlus,
  Edit3,
  UserX,
  UserCheck,
  Shield,
  User,
  AlertCircle,
  Search,
  Trash2,
  Home,
} from 'lucide-react';

const UserList = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterActive, setFilterActive] = useState('all');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Fetching users...');

      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterRole !== 'all') params.role = filterRole;
      if (filterActive !== 'all') params.active = filterActive;

      // Try to fetch users first
      const usersResponse = await usersAPI.getUsers(params);
      console.log('✅ Users loaded:', usersResponse.data);
      setUsers(usersResponse.data.users || []);

      // Try to fetch stats, but don't fail if it doesn't work
      try {
        const statsResponse = await usersAPI.getUserStats();
        console.log('✅ Stats loaded:', statsResponse.data);
        setStats(statsResponse.data.statistics || null);
      } catch (statsError) {
        console.warn('⚠️ Could not load user stats:', statsError);
        // Set default stats
        setStats({
          total_users: usersResponse.data.users?.length || 0,
          active_users: usersResponse.data.users?.filter(u => u.is_active).length || 0,
          admin_users: usersResponse.data.users?.filter(u => u.role === 'admin').length || 0,
          regular_users: usersResponse.data.users?.filter(u => u.role === 'user').length || 0,
          recent_users_30_days: 0
        });
      }

    } catch (error) {
      console.error('❌ Error fetching users:', error);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
      setError(error.response?.data?.message || error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterRole, filterActive]);

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
    }
  }, [fetchUsers, isAdmin]);

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      if (userId === user.id) {
        alert('You cannot deactivate your own account!');
        return;
      }

      if (currentStatus) {
        await usersAPI.deleteUser(userId);
      } else {
        await usersAPI.activateUser(userId);
      }

      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setModalLoading(false);
  };

  const handleSaveUser = async (userData) => {
    try {
      setModalLoading(true);

      if (editingUser) {
        // Update existing user
        console.log('🔄 Updating user:', editingUser.id, userData);
        await usersAPI.updateUser(editingUser.id, userData);
        console.log('✅ User updated successfully');
      } else {
        // Create new user
        console.log('🔄 Creating new user:', userData);
        await usersAPI.createUser(userData);
        console.log('✅ User created successfully');
      }

      handleCloseModal();
      fetchUsers(); // Refresh the list

    } catch (error) {
      console.error('❌ Error saving user:', error);
      alert(error.response?.data?.message || 'Failed to save user');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteUser = (userToDelete) => {
    if (userToDelete.id === user.id) {
      alert('You cannot delete your own account!');
      return;
    }
    setDeletingUser(userToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingUser(null);
    setDeleteLoading(false);
  };

  const handleConfirmDelete = async (userId) => {
    try {
      setDeleteLoading(true);
      console.log('🔄 Deleting user:', userId);

      // Use the delete endpoint - this will permanently delete the user
      await usersAPI.deleteUser(userId);
      console.log('✅ User deleted successfully');

      handleCloseDeleteModal();
      fetchUsers(); // Refresh the list

    } catch (error) {
      console.error('❌ Error deleting user:', error);
      alert(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Check if user is admin - moved after hooks
  if (!isAdmin()) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="error-message">
            <Shield size={24} />
            <div>
              <h3>Access Denied</h3>
              <p className="error-text">You need admin privileges to access user management.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="error-message">
            <AlertCircle size={24} />
            <div>
              <h3>Error Loading Users</h3>
              <p className="error-text">{error}</p>
              <button
                onClick={fetchUsers}
                className="btn-primary"
                style={{marginTop: '1rem', width: 'auto'}}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1 className="header-title">User Management</h1>
            <p className="header-subtitle">Manage system users and permissions</p>
          </div>

          <div className="header-actions">
            <Link to="/dashboard" className="btn-secondary">
              <Home size={16} />
              <span>Dashboard</span>
            </Link>

            <button
              onClick={fetchUsers}
              disabled={loading}
              className="btn-secondary"
            >
              <Users size={16} />
              <span>Refresh</span>
            </button>

            <button
              onClick={handleAddUser}
              className="btn-secondary"
            >
              <UserPlus size={16} />
              <span>Add User</span>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Stats Cards */}
        {stats && (
          <div className="cards-grid">
            <div className="dashboard-card">
              <div className="card-content">
                <div className="card-info">
                  <p className="card-title">Total Users</p>
                  <p className="card-value">{stats.total_users}</p>
                  <p className="card-subtitle">{stats.active_users} active</p>
                </div>
                <div className="card-icon icon-blue">
                  <Users size={24} />
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-content">
                <div className="card-info">
                  <p className="card-title">Admin Users</p>
                  <p className="card-value">{stats.admin_users}</p>
                  <p className="card-subtitle">System administrators</p>
                </div>
                <div className="card-icon icon-red">
                  <Shield size={24} />
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-content">
                <div className="card-info">
                  <p className="card-title">Regular Users</p>
                  <p className="card-value">{stats.regular_users}</p>
                  <p className="card-subtitle">Standard access</p>
                </div>
                <div className="card-icon icon-green">
                  <User size={24} />
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-content">
                <div className="card-info">
                  <p className="card-title">Recent Users</p>
                  <p className="card-value">{stats.recent_users_30_days}</p>
                  <p className="card-subtitle">Last 30 days</p>
                </div>
                <div className="card-icon icon-yellow">
                  <UserPlus size={24} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="dashboard-card" style={{marginBottom: '2rem'}}>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
            <div style={{position: 'relative', minWidth: '200px'}}>
              <Search size={16} style={{position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.5rem 0.5rem 2.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="dashboard-card">
          <div className="section-title" style={{marginBottom: '1.5rem'}}>
            <Users size={20} />
            System Users ({filteredUsers.length})
          </div>

          {loading ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>
              <div className="loading-spinner" style={{margin: '0 auto'}}></div>
              <p style={{marginTop: '1rem', color: '#6b7280'}}>Loading users...</p>
            </div>
          ) : (
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>User</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Role</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Status</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Last Login</th>
                    <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Created</th>
                    <th style={{padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} style={{borderBottom: '1px solid #f3f4f6'}}>
                      <td style={{padding: '0.75rem'}}>
                        <div>
                          <div style={{fontWeight: '500', color: '#1f2937'}}>{u.full_name}</div>
                          <div style={{fontSize: '0.875rem', color: '#6b7280'}}>@{u.username}</div>
                          {u.email && <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>{u.email}</div>}
                        </div>
                      </td>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: u.role === 'admin' ? '#fef2f2' : '#f0fdf4',
                          color: u.role === 'admin' ? '#dc2626' : '#16a34a'
                        }}>
                          {u.role === 'admin' ? <Shield size={12} /> : <User size={12} />}
                          {u.role}
                        </span>
                      </td>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: u.is_active ? '#f0fdf4' : '#fef2f2',
                          color: u.is_active ? '#16a34a' : '#dc2626'
                        }}>
                          {u.is_active ? <UserCheck size={12} /> : <UserX size={12} />}
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280'}}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280'}}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td style={{padding: '0.75rem', textAlign: 'center'}}>
                        <div style={{display: 'flex', justifyContent: 'center', gap: '0.5rem'}}>
                          <button
                            onClick={() => handleEditUser(u)}
                            style={{
                              padding: '0.25rem',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              cursor: 'pointer'
                            }}
                            title="Edit user"
                          >
                            <Edit3 size={14} />
                          </button>

                          {u.id !== user.id && (
                            <>
                              <button
                                onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                                style={{
                                  padding: '0.25rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: u.is_active ? '#fef2f2' : '#f0fdf4',
                                  color: u.is_active ? '#dc2626' : '#16a34a',
                                  cursor: 'pointer'
                                }}
                                title={u.is_active ? 'Deactivate user' : 'Activate user'}
                              >
                                {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                              </button>

                              <button
                                onClick={() => handleDeleteUser(u)}
                                style={{
                                  padding: '0.25rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#fef2f2',
                                  color: '#dc2626',
                                  cursor: 'pointer'
                                }}
                                title="Delete user permanently"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && !loading && (
                <div className="empty-state">
                  <Users size={48} style={{opacity: 0.5, margin: '0 auto 0.75rem'}} />
                  <p>No users found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        user={editingUser}
        loading={modalLoading}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        user={deletingUser}
        loading={deleteLoading}
      />
    </div>
  );
};

export default UserList;